import {
  EnvironmentRuntime,
  EnvironmentStatus,
  ProjectEnvironment,
} from '../../services/projectEnvironment';

/** Which screen the panel is showing. */
export type View =
  | { kind: 'list' }
  | { kind: 'details'; environment: ProjectEnvironment }
  | { kind: 'form'; environment?: ProjectEnvironment };

/** Backend rule: lowercase letters, digits and hyphens only, no whitespace. */
export const ENVIRONMENT_NAME_PATTERN = /^[a-z0-9-]+$/;

/** Kept in sync with the backend. 24 (project id) + 1 (hyphen) + 38 = 63, the
 *  DNS label limit — so the id-prefixed default URL always fits. */
export const ENVIRONMENT_NAME_MAX_LENGTH = 38;

/** A custom domain follows the same rules as a project's. */
export const CUSTOM_DOMAIN_PATTERN = /^[a-z0-9-]+$/;
export const CUSTOM_DOMAIN_MAX_LENGTH = 63;

/** A background task owns the record — poll and disable actions. Only deploy
 * has such a state: create finishes inside its own request. */
export const isBusyStatus = (status: EnvironmentStatus): boolean =>
  status === 'deploying';

/** The last deploy failed. Create cannot leave a failed record behind — it
 * either returns an environment or an error. */
export const isFailedStatus = (status: EnvironmentStatus): boolean =>
  status === 'deploy_failed';

/** Has this environment ever been deployed? Everything physical — folder,
 * containers, a URL that resolves — exists only from that point, so the
 * runtime badge and the start/stop controls are meaningless before it. */
export const isDeployed = (status: EnvironmentStatus): boolean =>
  status === 'deployed';

/** Names are stored lowercase — capitalize the first letter for display only. */
export const displayEnvironmentName = (name: string): string =>
  name.charAt(0).toUpperCase() + name.slice(1);

/** Short display label for an environment URL — the '<project or domain>-<name>'
 * hostname segment, without protocol and domain suffix. */
export const urlLabel = (url: string): string => {
  try {
    return new URL(url).hostname.split('.')[0];
  } catch {
    return url;
  }
};

/** Which lifecycle action the runtime state allows right now, if any.
 * null while a start/stop is in flight or when Docker is unreachable.
 *
 * A never-deployed environment reports 'stopped' — it has no containers — but
 * Start cannot help it: there is nothing built to start, and the backend
 * answers 400 "Deploy this environment before starting it." Deploy is the only
 * action that moves it forward, so no lifecycle action is offered. */
export const runtimeAction = (
  runtime: EnvironmentRuntime | undefined,
  status: EnvironmentStatus
): 'start' | 'stop' | null => {
  if (!isDeployed(status)) return null;
  if (!runtime || runtime.in_progress) return null;
  if (runtime.status === 'running' || runtime.status === 'partial') return 'stop';
  if (runtime.status === 'stopped' || runtime.status === 'error') return 'start';
  return null;
};

/** Pull a human-readable message out of an axios error, falling back to a default.
 *  Deploy errors arrive as a dict body ({detail: {detail, direction, ...}}) so the
 *  refusal can carry structured fields alongside the message. */
export const extractErrorDetail = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (typeof detail?.detail === 'string') return detail.detail;
  if (typeof detail?.message === 'string') return detail.message;
  return fallback;
};
